/** Formatting + stage model for the Pipeline jobs page. */

export function formatDuration(seconds) {
  const n = Math.round(Number(seconds));
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return `${n} s`;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  if (h) return m ? `${h} h ${m} min` : `${h} h`;
  return s && m < 10 ? `${m} min ${s} s` : `${m} min`;
}

/** "About 23 min left" — whole minutes, never "0 min". */
export function formatLeft(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  const mins = Math.max(1, Math.round(n / 60));
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  return `${mins} min`;
}

export function clockTime(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function parseIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function elapsedSeconds(startedAt, now = Date.now()) {
  const d = parseIso(startedAt);
  return d ? Math.max(0, Math.round((now - d.getTime()) / 1000)) : null;
}

/** "10/01/2025" or "2025-10-01" → "IEP Oct 1, 2025". */
export function cycleLabel(anchor) {
  if (!anchor) return 'IEP cycle';
  const s = String(anchor);
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const d = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
    return `IEP ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return `IEP ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return `IEP ${s}`;
}

export function shortCycle(anchor) {
  const s = String(anchor || '');
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const d = new Date(Number(mdy[3]), Number(mdy[1]) - 1, 1);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, 1);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  return s || 'Cycle';
}

export function jobTypeLabel(job) {
  const t = String(job?.job_type || '').toLowerCase();
  if (t.includes('section')) return `Section re-run${job?.section_label ? ` · ${job.section_label}` : ''}`;
  if (t.includes('rerun') || t.includes('merged')) return 'Compliance re-run';
  if (t.includes('upload') || t.includes('process') || t.includes('document')) return 'New upload';
  return t ? t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Job';
}

export function isRerunJob(job) {
  const t = String(job?.job_type || '').toLowerCase();
  return t.includes('rerun') || t.includes('merged') || t.includes('section');
}

/** Pipeline stages in run order; a re-run starts at Compliance. */
export const STAGES = [
  { key: 'ocr', label: 'Read pages', phases: ['ocr'] },
  { key: 'supplement', label: 'Forms & scores', phases: ['supplement'] },
  { key: 'extraction', label: 'Extract data', phases: ['extraction', 'prep'] },
  { key: 'embedding', label: 'Index', phases: ['embedding'] },
  { key: 'compliance', label: 'Compliance', phases: ['compliance'] },
  { key: 'save', label: 'Save results', phases: ['finalize', 'saving'] },
];

export function stagesForJob(job) {
  const list = isRerunJob(job) ? STAGES.filter((s) => s.key === 'compliance' || s.key === 'save') : STAGES;
  const phase = String(job?.phase || '').toLowerCase();
  const status = String(job?.status || '').toLowerCase();
  let current = list.findIndex((s) => s.phases.includes(phase));
  if (status === 'saving') current = list.length - 1;
  if (current < 0) current = status === 'queued' ? -1 : 0;
  return list.map((s, i) => ({
    ...s,
    state: i < current ? 'done' : i === current ? 'running' : 'waiting',
  }));
}

export function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}
